import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const steps = [
  { id: 1, title: "Create Your Event", desc: "Set up your personalized event page in seconds with custom themes." },
  { id: 2, title: "Share the QR Code", desc: "Display your unique code on tables, invites, or screens." },
  { id: 3, title: "Guests Scan & Upload", desc: "Attendees snap and upload instantly without downloading an app." },
  { id: 4, title: "Relive the Magic", desc: "Watch the gallery grow in real-time and download high-res memories." }
];

const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-24 bg-gray-950 text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-6">
              Automated Media <br />
              <span className="text-indigo-400">Collection Engine</span>
            </h2>
            <p className="text-gray-400 text-lg mb-12">
              Eliminate the friction of collecting photos. No more chasing hashtags or texting individual files. Memories automates the collection process so you can focus on the event.
            </p>
            <div className="space-y-8">
              {steps.map((step) => (
                <div key={step.id} className="flex items-start group">
                  <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-indigo-600 text-white font-bold border-2 border-indigo-500 group-hover:scale-110 transition-transform">
                    {step.id}
                  </div>
                  <div className="ml-6">
                    <h4 className="text-xl font-bold text-white mb-2">{step.title}</h4>
                    <p className="text-gray-400">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            {/* Real-time Sync Visualization */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-gray-800 bg-gray-900/50 p-8">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-transparent pointer-events-none"></div>
              
              {/* Central Hub */}
              <div className="relative z-10 flex flex-col items-center">
                <motion.div 
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="w-20 h-20 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-[0_0_40px_rgba(79,70,229,0.4)] mb-8"
                >
                  <SafeIcon icon={FiIcons.FiCamera} className="w-10 h-10 text-white" />
                </motion.div>

                {/* Grid of "Incoming" Photos */}
                <div className="grid grid-cols-3 gap-4 w-full">
                  {[...Array(6)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.1 }}
                      className="aspect-square bg-gray-800 rounded-xl border border-white/5 relative overflow-hidden group"
                    >
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute bottom-2 left-2 flex items-center gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></div>
                        <span className="text-[8px] font-bold text-white uppercase tracking-tighter">Live</span>
                      </div>
                      <div className="flex items-center justify-center h-full opacity-20 group-hover:opacity-40 transition-opacity">
                         <SafeIcon icon={FiIcons.FiImage} className="w-6 h-6 text-indigo-400" />
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Connection Lines Illustration */}
                <div className="mt-8 pt-8 border-t border-white/5 w-full">
                  <div className="flex justify-between items-center text-[10px] font-bold tracking-widest text-gray-500 uppercase">
                    <span>Syncing via QR</span>
                    <span className="text-indigo-400">99.9% Uptime</span>
                  </div>
                  <div className="mt-4 h-1.5 w-full bg-gray-800 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="h-full bg-indigo-500"
                    />
                  </div>
                </div>
              </div>

              {/* Floaties */}
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-4 -right-4 p-4 bg-gray-800 border border-white/10 rounded-2xl shadow-xl"
              >
                <SafeIcon icon={FiIcons.FiQrCode} className="w-8 h-8 text-indigo-400" />
              </motion.div>
            </div>

            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-500/20 blur-[120px] -z-0"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;